import React, { useState } from 'react';
import { GitCompare, FileText, ArrowRight, AlertTriangle, CheckCircle2, ShieldAlert, Lightbulb, Globe, BadgeCheck, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { runArticleAnalysis } from '@/lib/mlClient';
import { MLArticleInsights, PlainSummary, MLReasoning } from './MLInsights';
import { explainComparison } from '@/lib/plainExplanation';
import { isTrustedSource, parseDomain } from '@/lib/trustedSources';
import { useApp } from '@/context/AppContext';
import { cn } from '@/lib/utils';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from 'recharts';

const ScoreRingSmall = ({ score, label, size = 100, strokeWidth = 10 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;

  const getColor = () => {
    if (score >= 80) return "hsl(var(--success))";
    if (score >= 50) return "hsl(var(--warning))";
    return "hsl(var(--destructive))";
  };

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="hsl(var(--muted))" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={getColor()} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-700 ease-out" />
      </svg>
      <div className="absolute flex flex-col items-center justify-center">
        <span className="text-xl font-bold font-display" style={{ color: getColor() }}>{score}</span>
        <span className="text-[10px] text-muted-foreground">{label}</span>
      </div>
    </div>
  );
};

const ComparisonResult = ({ resultA, resultB }) => {
  const getConfig = (category) => {
    switch (category) {
      case 'High Credibility': return { color: 'text-success', badge: 'safe', icon: CheckCircle2 };
      case 'Mixed / Uncertain': return { color: 'text-warning', badge: 'suspicious', icon: AlertTriangle };
      case 'Low Credibility': return { color: 'text-destructive', badge: 'danger', icon: ShieldAlert };
      default: return { color: 'text-muted-foreground', badge: 'unknown', icon: Lightbulb };
    }
  };

  const configA = getConfig(resultA.category);
  const configB = getConfig(resultB.category);

  const barData = [
    { name: 'Conspiracy', 'Article A': resultA.summary.conspiracyCount, 'Article B': resultB.summary.conspiracyCount },
    { name: 'Emotional', 'Article A': resultA.summary.emotionalCount, 'Article B': resultB.summary.emotionalCount },
    { name: 'Sources', 'Article A': resultA.summary.sourceCount, 'Article B': resultB.summary.sourceCount },
    { name: 'Experts', 'Article A': resultA.summary.expertCount, 'Article B': resultB.summary.expertCount },
    { name: 'Sensationalist', 'Article A': resultA.summary.sensationalistCount, 'Article B': resultB.summary.sensationalistCount },
    { name: 'Absolute Claims', 'Article A': resultA.summary.absoluteClaimCount, 'Article B': resultB.summary.absoluteClaimCount },
  ];

  const radarData = [
    { signal: 'Sources', A: Math.min(resultA.summary.sourceCount * 20, 100), B: Math.min(resultB.summary.sourceCount * 20, 100) },
    { signal: 'Experts', A: Math.min(resultA.summary.expertCount * 25, 100), B: Math.min(resultB.summary.expertCount * 25, 100) },
    { signal: 'Neutral Tone', A: Math.min(resultA.summary.neutralCount * 15, 100), B: Math.min(resultB.summary.neutralCount * 15, 100) },
    { signal: 'Emotional (inv)', A: Math.max(100 - resultA.summary.emotionalCount * 25, 0), B: Math.max(100 - resultB.summary.emotionalCount * 25, 0) },
    { signal: 'Conspiracy (inv)', A: Math.max(100 - resultA.summary.conspiracyCount * 30, 0), B: Math.max(100 - resultB.summary.conspiracyCount * 30, 0) },
    { signal: 'Sensational (inv)', A: Math.max(100 - resultA.summary.sensationalistCount * 20, 0), B: Math.max(100 - resultB.summary.sensationalistCount * 20, 0) },
  ];

  const winner = resultA.credibilityScore > resultB.credibilityScore ? 'A'
    : resultB.credibilityScore > resultA.credibilityScore ? 'B' : 'Tie';

  return (
    <div className="space-y-6">
      {/* Winner Banner */}
      <GlassCard className={cn("p-4 sm:p-6 border-2",
        winner === 'Tie' ? 'border-primary/30' :
        winner === 'A' ? 'border-success/30' : 'border-success/30'
      )}>
        <div className="text-center space-y-2">
          <h3 className="font-display text-lg sm:text-xl font-bold">
            {winner === 'Tie' ? "It's a Tie!" : `Article ${winner} is More Credible`}
          </h3>
          <p className="text-sm text-muted-foreground">
            {winner === 'Tie'
              ? 'Both articles have the same credibility score.'
              : `Article ${winner} scored ${Math.abs(resultA.credibilityScore - resultB.credibilityScore)} points higher.`}
          </p>
        </div>
      </GlassCard>

      {/* Score Comparison */}
      <div className="grid grid-cols-2 gap-4">
        <GlassCard className={cn("p-4 text-center border-2", configA.color === 'text-success' ? 'border-success/20' : configA.color === 'text-warning' ? 'border-warning/20' : 'border-destructive/20')}>
          <p className="text-xs text-muted-foreground mb-2 font-medium">Article A</p>
          <div className="flex justify-center mb-2">
            <ScoreRingSmall score={resultA.credibilityScore} label="Score" />
          </div>
          <Badge variant={configA.badge} className="text-xs" data-testid="article-a-badge">
            <configA.icon className="w-3 h-3 mr-1" />
            {resultA.category}
          </Badge>
          {resultA.sourceDomain && (
            <div className="mt-2 flex items-center justify-center gap-1 text-[11px] sm:text-xs" data-testid="article-a-source">
              {resultA.trustedSource ? (
                <BadgeCheck className="w-3.5 h-3.5 text-success flex-shrink-0" />
              ) : (
                <Globe className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              )}
              <span className="text-muted-foreground">Source:</span>
              <span className={cn(
                "font-mono truncate max-w-[140px]",
                resultA.trustedSource ? "text-success font-medium" : "text-foreground"
              )}>
                {resultA.sourceDomain}
              </span>
            </div>
          )}
        </GlassCard>

        <GlassCard className={cn("p-4 text-center border-2", configB.color === 'text-success' ? 'border-success/20' : configB.color === 'text-warning' ? 'border-warning/20' : 'border-destructive/20')}>
          <p className="text-xs text-muted-foreground mb-2 font-medium">Article B</p>
          <div className="flex justify-center mb-2">
            <ScoreRingSmall score={resultB.credibilityScore} label="Score" />
          </div>
          <Badge variant={configB.badge} className="text-xs" data-testid="article-b-badge">
            <configB.icon className="w-3 h-3 mr-1" />
            {resultB.category}
          </Badge>
          {resultB.sourceDomain && (
            <div className="mt-2 flex items-center justify-center gap-1 text-[11px] sm:text-xs" data-testid="article-b-source">
              {resultB.trustedSource ? (
                <BadgeCheck className="w-3.5 h-3.5 text-success flex-shrink-0" />
              ) : (
                <Globe className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              )}
              <span className="text-muted-foreground">Source:</span>
              <span className={cn(
                "font-mono truncate max-w-[140px]",
                resultB.trustedSource ? "text-success font-medium" : "text-foreground"
              )}>
                {resultB.sourceDomain}
              </span>
            </div>
          )}
        </GlassCard>
      </div>

      {/* Signal Breakdown Bar Chart */}
      <GlassCard className="p-4 sm:p-6">
        <h4 className="text-sm font-semibold mb-4">Signal Breakdown</h4>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={barData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
            <Tooltip
              contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }}
              labelStyle={{ color: 'hsl(var(--foreground))' }}
            />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            <Bar dataKey="Article A" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Article B" fill="hsl(var(--warning))" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </GlassCard>

      {/* Radar Chart */}
      <GlassCard className="p-4 sm:p-6">
        <h4 className="text-sm font-semibold mb-4">Credibility Radar</h4>
        <ResponsiveContainer width="100%" height={300}>
          <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
            <PolarGrid stroke="hsl(var(--border))" />
            <PolarAngleAxis dataKey="signal" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
            <PolarRadiusAxis tick={false} domain={[0, 100]} />
            <Radar name="Article A" dataKey="A" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.2} />
            <Radar name="Article B" dataKey="B" stroke="hsl(var(--warning))" fill="hsl(var(--warning))" fillOpacity={0.2} />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
          </RadarChart>
        </ResponsiveContainer>
      </GlassCard>

      {/* Top Issues Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[{ label: 'Article A', result: resultA }, { label: 'Article B', result: resultB }].map(({ label, result }) => (
          <GlassCard key={label} className="p-4">
            <h4 className="text-sm font-semibold mb-3">{label} — Top Issues</h4>
            {result.topReasons.length === 0 ? (
              <p className="text-xs text-muted-foreground">No significant issues found.</p>
            ) : (
              <div className="space-y-2">
                {result.topReasons.slice(0, 3).map((flag, i) => (
                  <div key={i} className={cn("p-2 rounded-lg border text-xs",
                    flag.severity === 'critical' ? 'bg-destructive/10 border-destructive/30' :
                    flag.severity === 'high' ? 'bg-warning/10 border-warning/30' :
                    'bg-muted/50 border-border'
                  )}>
                    <div className="flex justify-between">
                      <span className="font-medium">{flag.title}</span>
                      <span className="font-mono text-destructive">-{flag.penalty}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>
        ))}
      </div>
    </div>
  );
};

export const ArticleComparison = () => {
  const [textA, setTextA] = useState('');
  const [textB, setTextB] = useState('');
  const [sourceUrlA, setSourceUrlA] = useState('');
  const [sourceUrlB, setSourceUrlB] = useState('');
  const [resultA, setResultA] = useState(null);
  const [resultB, setResultB] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { addVerification } = useApp();

  const domainA = sourceUrlA.trim() ? parseDomain(sourceUrlA) : '';
  const domainB = sourceUrlB.trim() ? parseDomain(sourceUrlB) : '';
  const trustedA = sourceUrlA.trim() ? isTrustedSource(sourceUrlA) : false;
  const trustedB = sourceUrlB.trim() ? isTrustedSource(sourceUrlB) : false;

  const handleCompare = async () => {
    if (!textA.trim() || !textB.trim()) return;
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    const [rA, rB] = await Promise.all([
      runArticleAnalysis(textA, sourceUrlA),
      runArticleAnalysis(textB, sourceUrlB),
    ]);
    setResultA(rA);
    setResultB(rB);

    const winner = rA.credibilityScore > rB.credibilityScore ? 'A'
      : rB.credibilityScore > rA.credibilityScore ? 'B' : 'Tie';
    const labelA = rA.sourceDomain ? `${rA.sourceDomain}` : 'A';
    const labelB = rB.sourceDomain ? `${rB.sourceDomain}` : 'B';
    const summaryInput = `Compared: ${labelA}=${rA.credibilityScore}/100 (${rA.category}) vs ${labelB}=${rB.credibilityScore}/100 (${rB.category}) — ${winner === 'Tie' ? 'Tie' : `Article ${winner} more credible`}`;
    const avgScore = Math.round((rA.credibilityScore + rB.credibilityScore) / 2);
    const overallVerdict = winner === 'Tie' ? 'Tie' : `Article ${winner} wins`;

    addVerification({
      type: 'article-comparison',
      input: summaryInput,
      verdict: overallVerdict,
      credibilityScore: avgScore,
      flagCount: (rA.flags.filter(f => f.type !== 'positive').length + rB.flags.filter(f => f.type !== 'positive').length),
      result: {
        articleA: {
          score: rA.credibilityScore,
          category: rA.category,
          preview: textA.substring(0, 80) + (textA.length > 80 ? '…' : ''),
          sourceUrl: rA.sourceUrl,
          sourceDomain: rA.sourceDomain,
          trustedSource: rA.trustedSource,
        },
        articleB: {
          score: rB.credibilityScore,
          category: rB.category,
          preview: textB.substring(0, 80) + (textB.length > 80 ? '…' : ''),
          sourceUrl: rB.sourceUrl,
          sourceDomain: rB.sourceDomain,
          trustedSource: rB.trustedSource,
        },
        winner,
      },
    });

    setIsLoading(false);
  };

  const canCompare = textA.trim().length >= 50 && textB.trim().length >= 50;

  const exampleA = "SHOCKING: Insiders claim the government is HIDING the truth about 5G! Sources say 87% of scientists are being suppressed. Act now before it's too late! They don't want you to know this devastating secret!";
  const exampleB = "According to a peer-reviewed study published in Nature Medicine, researchers at Stanford University found that regular exercise can improve cognitive function. The research, conducted over five years by Dr. Sarah Chen, indicates that participants who exercised three times weekly showed measurable improvements in memory tests. However, experts say more research is needed to confirm these findings.";

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 sm:gap-3 mb-2">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
            <GitCompare className="w-4 h-4 sm:w-5 sm:h-5 text-primary-foreground" />
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-foreground">Article Comparison</h2>
        </div>
        <p className="text-sm sm:text-base text-muted-foreground">
          Compare two articles side-by-side to see which is more credible.
        </p>
      </div>

      {/* Input Areas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GlassCard className="p-4 sm:p-5">
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold">Article A</span>
              {textA.length > 0 && (
                <span className="text-xs text-muted-foreground ml-auto">{textA.split(/\s+/).filter(w => w).length} words</span>
              )}
            </div>
            <Textarea
              placeholder="Paste first article text..."
              value={textA}
              onChange={(e) => setTextA(e.target.value)}
              className="min-h-[160px] text-sm resize-none"
              data-testid="compare-article-a-input"
            />
            {textA.trim().length > 0 && textA.trim().length < 50 && (
              <p className="text-xs text-warning">Minimum 50 characters required.</p>
            )}

            {/* Optional Source URL A */}
            <div className="space-y-1.5 pt-1">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" />
                Article A Source URL <span className="text-muted-foreground/60">(optional)</span>
              </label>
              <Input
                type="url"
                inputMode="url"
                placeholder="https://www.bbc.com/news/..."
                value={sourceUrlA}
                onChange={(e) => setSourceUrlA(e.target.value)}
                className="h-10 text-sm"
                data-testid="compare-source-url-a-input"
              />
              {domainA && (
                <div className="flex items-center gap-1.5 text-[11px]">
                  {trustedA ? (
                    <span className="inline-flex items-center gap-1 text-success" data-testid="compare-source-a-trusted">
                      <BadgeCheck className="w-3.5 h-3.5" />
                      Trusted: <span className="font-mono">{domainA}</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-muted-foreground" data-testid="compare-source-a-unknown">
                      <Info className="w-3.5 h-3.5" />
                      Unknown: <span className="font-mono">{domainA}</span>
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-4 sm:p-5">
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-4 h-4 text-warning" />
              <span className="text-sm font-semibold">Article B</span>
              {textB.length > 0 && (
                <span className="text-xs text-muted-foreground ml-auto">{textB.split(/\s+/).filter(w => w).length} words</span>
              )}
            </div>
            <Textarea
              placeholder="Paste second article text..."
              value={textB}
              onChange={(e) => setTextB(e.target.value)}
              className="min-h-[160px] text-sm resize-none"
              data-testid="compare-article-b-input"
            />
            {textB.trim().length > 0 && textB.trim().length < 50 && (
              <p className="text-xs text-warning">Minimum 50 characters required.</p>
            )}

            {/* Optional Source URL B */}
            <div className="space-y-1.5 pt-1">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" />
                Article B Source URL <span className="text-muted-foreground/60">(optional)</span>
              </label>
              <Input
                type="url"
                inputMode="url"
                placeholder="https://example.com/article"
                value={sourceUrlB}
                onChange={(e) => setSourceUrlB(e.target.value)}
                className="h-10 text-sm"
                data-testid="compare-source-url-b-input"
              />
              {domainB && (
                <div className="flex items-center gap-1.5 text-[11px]">
                  {trustedB ? (
                    <span className="inline-flex items-center gap-1 text-success" data-testid="compare-source-b-trusted">
                      <BadgeCheck className="w-3.5 h-3.5" />
                      Trusted: <span className="font-mono">{domainB}</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-muted-foreground" data-testid="compare-source-b-unknown">
                      <Info className="w-3.5 h-3.5" />
                      Unknown: <span className="font-mono">{domainB}</span>
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <Button
          onClick={handleCompare}
          disabled={!canCompare || isLoading}
          className="w-full sm:w-auto h-12 text-base px-8"
          variant="gradient"
          data-testid="compare-button"
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
          ) : (
            <>
              <GitCompare className="w-5 h-5 mr-2" />
              Compare Articles
            </>
          )}
        </Button>

        <button
          onClick={() => { setTextA(exampleA); setTextB(exampleB); }}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
          data-testid="load-examples-btn"
        >
          Load example articles
        </button>
      </div>

      {/* Results */}
      {resultA && resultB && (
        <>
          <PlainSummary summary={explainComparison(resultA, resultB)} testId="compare-plain-summary" />
          <ComparisonResult resultA={resultA} resultB={resultB} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground">Article A</p>
              <MLReasoning testId="compare-a-ml-toggle">
                <MLArticleInsights ml={resultA.ml} />
              </MLReasoning>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground">Article B</p>
              <MLReasoning testId="compare-b-ml-toggle">
                <MLArticleInsights ml={resultB.ml} />
              </MLReasoning>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ArticleComparison;
